import React, { useLayoutEffect } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import PageList from '~/pages/components/PageList';
import PageEditor from '~/pages/components/PageEditor';
import PageAnalytics from '~/pages/components/PageAnalytics';
import CreatePage from '~/pages/components/CreatePage';
import EditorTypeSelector from '~/pages/components/EditorTypeSelector';
import CopyPage from '~/pages/components/CopyPage';
import EditPageMeta from '~/pages/components/EditPageMeta';
import VersionsScreen from '~/pages/components/VersionsScreen';
import CategoriesScreen from '~/categories/components/CategoriesScreen';
import TemplateBrowser from '~/templates/components/TemplateBrowser';
import PeopleScreen from '~/roles/components/PeopleScreen';
import EditRole from '~/roles/components/EditRole';
import AddUser from '~/roles/components/AddUser';
import ApprovalsScreen from '~/approvals/components/ApprovalsScreen';
import ReviewPage from '~/approvals/components/ReviewPage';
import Deploy from '~/deploy/components/DeployContainer';
import SettingsScreen from '~/settings/components/SettingsScreen';
import AssetsScreen from '~/assets/components/AssetsScreen';
import PresetsScreen from '~/presets/components/PresetsScreen';
import SuperAdminRoute from './SuperAdminRoute';
import AppShell from './shell/AppShell';
import { isFocusRoute } from './shell/navModel';
import { WidgetList, WidgetBuilder } from '~/widgets';
import AiGuideScreen from '~/aiGuide/AiGuideScreen';
import ConnectScreen from '~/connect/ConnectScreen';
import { screenTitle } from 'shared/lib/aiGuide/screens';


function Admin({ match }) {
  const { pathname } = useLocation();
  // One tab title per screen ("Pages · Genesis"), so people and AI assistants can tell tabs apart.
  // A layout effect runs before the screens' own effects, so a screen can name its tab more exactly (the editor adds the page title).
  useLayoutEffect(() => { document.title = screenTitle(pathname); }, [pathname]);
  const routes = (
    <Switch>
      <Route exact path={match.url} component={PageList} />
      <Route exact path={`${match.url}/template-browser`} component={TemplateBrowser} />
      <Route exact path={`${match.url}/page/new`} component={EditorTypeSelector} />
      {/* Keyed by navigation, so opening another create link (another ?category=) starts a fresh form. */}
      <Route exact path={`${match.url}/page/new/studio`} render={props => <CreatePage key={props.location.key} {...props} />} />
      <Route path={`${match.url}/page/new/:id`} render={props => <CreatePage key={props.location.key} {...props} />} />
      <Route path={`${match.url}/presets`} component={PresetsScreen} />
      <Route path={`${match.url}/page/copy/:id`} component={CopyPage} />
      <Route path={`${match.url}/page/versions/:id`} component={VersionsScreen} />
      <Route path={`${match.url}/page/:id/edit-meta`} render={({ match }) => <EditPageMeta pageId={match.params.id} />} />
      <Route path={`${match.url}/page/:id/analytics`} component={PageAnalytics} />
      <Route path={`${match.url}/page/:id/preview/:versionId`} component={PageEditor} />
      <Route path={`${match.url}/page/:id`} component={PageEditor} />
      <Route path={`${match.url}/review/:id`} component={ReviewPage} />
      <Route path={`${match.url}/approvals/:kind?`} component={ApprovalsScreen} />
      <SuperAdminRoute path={`${match.url}/roles`} component={PeopleScreen} />
      <SuperAdminRoute path={`${match.url}/role/add`} component={AddUser} />
      <SuperAdminRoute path={`${match.url}/role/:id`} component={EditRole} />
      <Route path={`${match.url}/categories`} component={CategoriesScreen} />
      <Route path={`${match.url}/settings`} component={SettingsScreen} />
      <Route path={`${match.url}/assets/:category(images|videos)?`} component={AssetsScreen} />
      <Route path={`${match.url}/deploy`} component={Deploy} />
      <Route exact path={`${match.url}/ai-guide`} component={AiGuideScreen} />
      <Route exact path={`${match.url}/connect`} component={ConnectScreen} />
      {/* Custom Widget Builder */}
      <Route exact path={`${match.url}/widgets`} component={WidgetList} />
      <Route path={`${match.url}/widgets/builder/:id`} component={WidgetBuilder} />
    </Switch>
  );
  // The editor, the widget builder and the review page use the whole window and have their own way back.
  if (isFocusRoute(pathname)) return <div style={getStyle()}>{routes}</div>;
  return <AppShell>{routes}</AppShell>;
}

const getStyle = () => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column'
});

export default Admin;
